<script setup>
import { computed, ref } from 'vue';
import Icon from 'next/icon/Icon.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useMessageContext } from '../provider.js';
import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';

const props = defineProps({
  attachment: {
    type: Object,
    required: true,
  },
});

const showGallery = ref(false);
const duration = ref(0);
const detectedAspectRatio = ref('');

const { filteredCurrentChatAttachments, contentAttributes } =
  useMessageContext();

const isGif = computed(() => {
  const mediaType =
    contentAttributes.value?.wameMediaType ||
    contentAttributes.value?.wame_media_type;
  return mediaType === 'gif';
});

const mediaAspectRatio = computed(() => {
  if (detectedAspectRatio.value) return detectedAspectRatio.value;

  const { width, height } = props.attachment || {};
  return Number(width) > 0 && Number(height) > 0
    ? `${width} / ${height}`
    : '16 / 9';
});

const durationLabel = computed(() => {
  if (!Number.isFinite(duration.value) || duration.value <= 0) return '';
  const totalSeconds = Math.floor(duration.value);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = String(totalSeconds % 60).padStart(2, '0');
  return hours
    ? `${hours}:${String(minutes).padStart(2, '0')}:${seconds}`
    : `${minutes}:${seconds}`;
});

const mediaDurationLabel = computed(() =>
  isGif.value ? 'GIF' : durationLabel.value
);

const handleLoadedMetadata = event => {
  const video = event.currentTarget;
  duration.value = Number(video?.duration) || 0;

  if (Number(video?.videoWidth) > 0 && Number(video?.videoHeight) > 0) {
    detectedAspectRatio.value = `${video.videoWidth} / ${video.videoHeight}`;
  }
};
</script>

<template>
  <div
    class="acl-media-chip acl-media-chip--video overflow-hidden contain-content cursor-pointer relative group"
    data-attachment-chip="video"
    :style="{ aspectRatio: mediaAspectRatio }"
    role="button"
    tabindex="0"
    @click="showGallery = true"
    @keydown.enter.space.self.prevent="showGallery = true"
  >
    <video
      :src="attachment.dataUrl"
      :poster="attachment.thumbUrl || undefined"
      :autoplay="isGif"
      :loop="isGif"
      muted
      playsinline
      preload="metadata"
      class="acl-media-chip__asset"
      @loadedmetadata="handleLoadedMetadata"
    />
    <div
      v-if="!isGif"
      class="acl-video-chip__play absolute inset-0 flex items-center justify-center"
    >
      <div class="acl-video-bubble__play-circle">
        <Icon icon="i-lucide-play" class="size-7" />
      </div>
    </div>
    <span v-if="mediaDurationLabel" class="acl-video-bubble__duration">
      <span v-if="!isGif" class="i-lucide-video size-3.5" />
      {{ mediaDurationLabel }}
    </span>
  </div>
  <GalleryView
    v-if="showGallery"
    v-model:show="showGallery"
    :attachment="useSnakeCase(attachment)"
    :all-attachments="filteredCurrentChatAttachments"
    @close="() => (showGallery = false)"
  />
</template>
